import { motion } from 'framer-motion';
import { Sparkles, Gift, Zap, Shield, Users } from 'lucide-react';
import { AppLang, t } from '../i18n';
import './WelcomeScreen.css';

interface WelcomeScreenProps {
  firstName: string;
  trialDays: number;
  lang: AppLang;
  onContinue: () => void;
}

export const WelcomeScreen = ({ firstName, trialDays, lang, onContinue }: WelcomeScreenProps) => {
  const dayWord = trialDays === 1 ? 'день' : trialDays < 5 ? 'дня' : 'дней';

  return (
    <motion.div
      className="welcome-screen"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="welcome-emoji"
        initial={{ scale: 0, rotate: -180 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ type: 'spring', stiffness: 260, damping: 20, delay: 0.2 }}
      >
        <motion.span
          animate={{ scale: [1, 1.2, 1] }}
          transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
        >
          <Sparkles size={32} strokeWidth={2.5} />
        </motion.span>
      </motion.div>

      <motion.h1
        className="welcome-title"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
      >
        {lang === 'en' ? 'Hi,' : 'Привет,'}
        <br />
        <span className="welcome-name">{firstName}!</span>
      </motion.h1>

      <motion.div
        className="bonus-card"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.6, type: 'spring' }}
      >
        <div className="bonus-icon">
          <Gift size={28} strokeWidth={2.5} />
        </div>

        <motion.div
          className="bonus-amount"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
        >
          {trialDays} {lang === 'en' ? (trialDays === 1 ? 'day' : 'days') : dayWord}
        </motion.div>

        <p className="bonus-text">
          {lang === 'en' ? 'free trial for new users' : 'бесплатно для новых пользователей'}
        </p>

        <div className="bonus-days">
          {lang === 'en' ? 'Full speed, no limits' : 'Полная скорость, без ограничений'}
        </div>
      </motion.div>

      <motion.div
        className="features-list"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
      >
        <div className="feature-item">
          <span className="feature-icon"><Zap size={22} strokeWidth={2.5} /></span>
          <span className="feature-text">{lang === 'en' ? 'Unlimited traffic' : 'Безлимитный трафик'}</span>
        </div>
        <div className="feature-item">
          <span className="feature-icon"><Shield size={22} strokeWidth={2.5} /></span>
          <span className="feature-text">{lang === 'en' ? 'Works with TikTok, ChatGPT, Instagram' : 'Работает с TikTok, ChatGPT, Instagram'}</span>
        </div>
        <div className="feature-item">
          <span className="feature-icon"><Users size={22} strokeWidth={2.5} /></span>
          <span className="feature-text">{lang === 'en' ? 'Plans from 299₽/mo' : 'Тарифы от 299₽/мес'}</span>
        </div>
      </motion.div>

      <motion.button
        className="btn-primary welcome-btn"
        onClick={onContinue}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.2 }}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        {t(lang, 'start')}
      </motion.button>

      <motion.button
        className="btn-ghost welcome-skip"
        onClick={onContinue}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
      >
        {t(lang, 'skip')}
      </motion.button>

      <div className="confetti-container">
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="confetti"
            initial={{ x: 200, y: -20, opacity: 1, rotate: 0 }}
            animate={{
              x: Math.random() * 400 - 200,
              y: window.innerHeight + 20,
              opacity: 0,
              rotate: Math.random() * 720 - 360,
            }}
            transition={{
              duration: 2 + Math.random() * 2,
              delay: 0.5 + Math.random() * 0.5,
              ease: 'easeOut',
            }}
            style={{
              left: `${50 + Math.random() * 20 - 10}%`,
              backgroundColor: i % 2 === 0 ? '#FFDD2D' : '#3F8CFF',
            }}
          />
        ))}
      </div>
    </motion.div>
  );
};
